<script setup lang="ts">
import {Icon, Spinner} from '@helcim/blizzard';
import {computed, ref, watch} from 'vue';
import {useCartStore} from '../../store/cart';
import Product from '../../store/classes/Product';

const props = defineProps<{
    product: Product & {typez: string, name: string},
}>();

const cartStore = useCartStore();

const clicked = ref(false);
const loading = ref(false);
const count = ref(cartStore.cartItemQuantities.get(props.product.id) ?? 0);
const lastCount = ref(count.value);
const max = ref(props.product.stock);
const timeout = ref<number | ReturnType<typeof setTimeout>>(0);

const addToCart = () => {
    if(count.value === lastCount.value) {
        clicked.value = false;
        return;
    }
    clicked.value = false;
    loading.value = true;
    cartStore.updateQuantity(props.product.id, count.value)
        .then(v => lastCount.value = v)
        .catch(() => count.value = lastCount.value)
        .finally(() => loading.value = false);
};

const setTimer = () => {
    if(timeout.value) {
        clearTimeout(timeout.value);
    }
    timeout.value = setTimeout(addToCart, 2000);
};

const handlePlus = () => {
    if(props.product.trackInventory && count.value + 1 > max.value) {
        count.value = max.value > 1 ? max.value : 0;
    } else {
        count.value++;
    }
    setTimer();
};

const handleMinus = () => {
    if(count.value - 1 < 0) {
        count.value = 0;
    } else {
        count.value--;
    }
    if(lastCount.value === 0 && count.value === 0) {
        clicked.value = false;
        clearTimeout(timeout.value);
        return;
    }
    setTimer();
};

const handleClick = () => {
    if(count.value === 0) {
        count.value++;
    }
    clicked.value = true;
    setTimer();
};

const quantity = computed(() => cartStore.cartItemQuantities.get(props.product.id) ?? 0);

watch(quantity, v => {
    count.value = v;
    lastCount.value = v;
});
</script>

<template>
    <template v-if="loading">
        <div class="d-flex justify-content-between align-items-center text-midnight-800 product-stepper-expanded">
            <Icon name="far fa-minus"/>
            <Spinner size="tall"/>
            <Icon name="far fa-plus"/>
        </div>
    </template>
    <template v-else-if="clicked">
        <div class="d-flex justify-content-between align-items-center text-midnight-800 product-stepper-expanded">
            <Icon name="far fa-minus" @click="handleMinus"/>
            <span class="tm16">{{ count }}</span>
            <Icon name="far fa-plus" @click="handlePlus"/>
        </div>
    </template>
    <template v-else-if="count">
        <div class="d-flex justify-content-center align-items-center bg-midnight-900 product-stepper-count">
            <span class="tb18 text-midnight-0" @click="handleClick">{{ count }}x</span>
        </div>
    </template>
    <template v-else>
        <div class="d-flex justify-content-center align-items-center product-stepper">
            <Icon class="text-midnight-800" name="far fa-plus" @click="handleClick"/>
        </div>
    </template>
</template>

<style scoped lang="scss">
.product-stepper-expanded {
    padding: 3px;
    background: white;
    border: 1px solid #DCD9E5;
    height: 36px; width: 104px;
    border-radius: 30px;
    box-shadow: 0 4px 4px rgba(49, 53, 91, 0.12);
}

.product-stepper-count {
    height: 36px;
    min-height: 36px;
    min-width: 36px;
    border-radius: 30px;
    box-shadow: 0 4px 4px rgba(49, 53, 91, 0.12);
}

.product-stepper {
    background: white; aspect-ratio: 1 / 1;
    border: 1px solid #DCD9E5;
    height: 36px; width: 36px;
    border-radius: 30px;
    box-shadow: 0 4px 4px rgba(49, 53, 91, 0.12);
}
</style>